import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography,
} from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { parseSiigoError } from "./siigoHelpers";
import SiigoErrorItem from "./SiigoErrorItem";

/**
 * Muestra de forma amigable un error devuelto por Siigo. Recibe el string crudo
 * (`rawError`), lo interpreta y resalta el Code y el Message de cada error.
 */
export default function SiigoErrorModal({ open, rawError, onClose }) {
  const [showTechnical, setShowTechnical] = useState(false);
  const [copied, setCopied] = useState(false);

  const parsed = useMemo(() => parseSiigoError(rawError), [rawError]);
  const hasStructuredErrors = parsed.errors.length > 0;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(parsed.raw || "");
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* El navegador puede bloquear el portapapeles; no es crítico. */
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{ display: "flex", alignItems: "center", gap: 1, color: "#b71c1c", pr: 6 }}
      >
        <ErrorOutlineIcon />
        No se pudo completar la operación
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ position: "absolute", right: 8, top: 8, color: "#666" }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" mb={2}>
          {hasStructuredErrors
            ? "Siigo rechazó la solicitud por los siguientes motivos:"
            : "Ocurrió un error al comunicarse con Siigo. Revisa el detalle técnico y comunícalo al área de sistemas si persiste."}
        </Typography>

        {hasStructuredErrors ? (
          parsed.errors.map((err, idx) => (
            <SiigoErrorItem key={`${err.code}-${idx}`} error={err} />
          ))
        ) : (
          <Alert severity="error" sx={{ mb: 1 }}>
            {parsed.raw || "Error desconocido."}
          </Alert>
        )}

        <Divider sx={{ my: 1 }} />

        <Button
          size="small"
          onClick={() => setShowTechnical((v) => !v)}
          endIcon={showTechnical ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          sx={{ textTransform: "none", color: "#161e55" }}
        >
          Detalle técnico (uso interno de sistemas)
        </Button>
        <Collapse in={showTechnical} timeout="auto" unmountOnExit>
          <Box sx={{ mt: 1 }}>
            {(parsed.httpStatus || parsed.method || parsed.url) && (
              <Box display="flex" gap={0.5} flexWrap="wrap" mb={1}>
                {parsed.httpStatus && (
                  <Chip label={parsed.httpStatus} size="small" color="error" variant="outlined" />
                )}
                {parsed.method && <Chip label={parsed.method} size="small" variant="outlined" />}
                {parsed.url && (
                  <Chip
                    label={parsed.url}
                    size="small"
                    variant="outlined"
                    sx={{ maxWidth: "100%" }}
                  />
                )}
              </Box>
            )}
            <Box
              component="pre"
              sx={{
                fontSize: "0.7rem",
                whiteSpace: "pre-wrap",
                wordBreak: "break-all",
                backgroundColor: "#fafafa",
                border: "1px solid #eee",
                borderRadius: "6px",
                p: 1,
                m: 0,
                maxHeight: 200,
                overflowY: "auto",
              }}
            >
              {parsed.raw}
            </Box>
          </Box>
        </Collapse>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={handleCopy}
          startIcon={<ContentCopyIcon fontSize="small" />}
          sx={{ textTransform: "none", color: "#161e55" }}
        >
          {copied ? "Copiado" : "Copiar detalle"}
        </Button>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{ textTransform: "none", backgroundColor: "#161e55", borderRadius: "20px" }}
        >
          Entendido
        </Button>
      </DialogActions>
    </Dialog>
  );
}
